import React, { useState, useEffect, useRef } from 'react';
import { HitApi } from '../../../Api/ApiHit';
import { getOnlineUsers } from '../../../constant/Constant';
import { Users, Smartphone, Apple, MonitorSmartphone, Search, RefreshCw, Monitor } from 'lucide-react';

const LiveUsers = () => {
  const [users, setUsers] = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [totalUniqueUsers, setTotalUniqueUsers] = useState(0);
  const [platformCounts, setPlatformCounts] = useState({ ios: 0, android: 0, unknown: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const intervalRef = useRef(null);
  const limit = 20;

  useEffect(() => {
    fetchOnlineUsers();
    // Auto-refresh every 10 seconds
    intervalRef.current = setInterval(fetchOnlineUsers, 10000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [page, search]);

  const fetchOnlineUsers = () => {
    setLoading(true);
    HitApi({ page, limit, search }, getOnlineUsers)
      .then((res) => {
        if (res?.success && res?.data) {
          setUsers(res.data.docs || []);
          setTotalSessions(res.data.totalSessions || 0);
          setTotalUniqueUsers(res.data.totalUniqueUsers || 0);
          setPlatformCounts(res.data.platformCounts || { ios: 0, android: 0, unknown: 0 });
          setTotalPages(res.data.totalPages || 1);
        }
      })
      .catch((err) => {
        console.error('Error fetching online users:', err);
      })
      .finally(() => setLoading(false));
  };

  const formatLastSeen = (date) => {
    if (!date) return '-';
    const diff = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (diff < 5) return 'Just now';
    if (diff < 60) return `${diff}s ago`;
    return `${Math.floor(diff / 60)}m ago`;
  };

  const getPlatformIcon = (platform) => {
    if (platform === 'ios') return <Apple className="w-3.5 h-3.5" />;
    if (platform === 'android') return <Smartphone className="w-3.5 h-3.5" />;
    return <MonitorSmartphone className="w-3.5 h-3.5" />;
  };

  const getPlatformLabel = (platform) => {
    if (platform === 'ios') return 'iOS';
    if (platform === 'android') return 'Android';
    return 'Unknown';
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Live Users</h1>
          <p className="text-sm text-gray-500 mt-1">Real-time active users on the app</p>
        </div>
        <button
          onClick={fetchOnlineUsers}
          className="flex items-center gap-2 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        {/* Unique Users */}
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-5 text-white shadow-lg shadow-emerald-500/20">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <Users className="w-5 h-5" />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
              </span>
              <span className="text-xs font-medium text-emerald-100">LIVE</span>
            </div>
          </div>
          <p className="text-3xl font-bold">{totalUniqueUsers}</p>
          <p className="text-sm text-emerald-100 mt-1">Unique Users</p>
        </div>

        {/* Active Sessions */}
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg shadow-blue-500/20">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
              <Monitor className="w-5 h-5" />
            </div>
          </div>
          <p className="text-3xl font-bold">{totalSessions}</p>
          <p className="text-sm text-blue-100 mt-1">Active Sessions</p>
        </div>

        {/* Android */}
        <div className="bg-white rounded-xl p-5 shadow border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-green-50 rounded-lg">
              <Smartphone className="w-5 h-5 text-green-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{platformCounts.android}</p>
          <p className="text-sm text-gray-500 mt-1">Android</p>
        </div>

        {/* iOS */}
        <div className="bg-white rounded-xl p-5 shadow border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Apple className="w-5 h-5 text-gray-700" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{platformCounts.ios}</p>
          <p className="text-sm text-gray-500 mt-1">iOS</p>
        </div>

        {/* Other */}
        <div className="bg-white rounded-xl p-5 shadow border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="p-2 bg-purple-50 rounded-lg">
              <MonitorSmartphone className="w-5 h-5 text-purple-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-900">{platformCounts.unknown}</p>
          <p className="text-sm text-gray-500 mt-1">Other</p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl shadow border border-gray-100 overflow-hidden">
        {loading && users.length === 0 ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-16">
            <Users className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-gray-500 text-sm font-medium">No users online right now</p>
            <p className="text-gray-400 text-xs mt-1">Users will appear here when they open the app</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">User</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Phone</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Device</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Platform</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-5 py-3">Last Seen</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {users.map((user) => (
                    <tr key={user._id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500 flex items-center justify-center text-white text-sm font-bold shadow-sm">
                            {(user.name || '?')[0].toUpperCase()}
                          </div>
                          <span className="font-medium text-gray-900 text-sm">{user.name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-gray-600 font-mono">{user.phoneNumber || '-'}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-xs text-gray-400 font-mono">{user.deviceId ? user.deviceId.slice(-8) : '-'}</span>
                      </td>
                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
                          user.platform === 'android' ? 'bg-green-50 text-green-700' :
                          user.platform === 'ios' ? 'bg-gray-100 text-gray-700' :
                          'bg-blue-50 text-blue-700'
                        }`}>
                          {getPlatformIcon(user.platform)}
                          {getPlatformLabel(user.platform)}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full">
                          <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                          </span>
                          Online
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <span className="text-sm text-gray-500">{formatLastSeen(user.lastSeen)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-gray-200 bg-gray-50/50">
                <span className="text-sm text-gray-600">
                  Page {page} of {totalPages} ({totalSessions} sessions, {totalUniqueUsers} users)
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Auto-refresh indicator */}
      <p className="text-xs text-gray-400 mt-3 text-center">Auto-refreshes every 10 seconds</p>
    </div>
  );
};

export default LiveUsers;
